import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import {
    ChakraProvider,
    theme as baseTheme,
    extendTheme,
} from '@chakra-ui/react';
import { IncomingMessage } from 'http';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import 'stream-chat-react/dist/css/v2/index.css';

import { apolloClient } from '@/client/startup/apollo-client';
import { ErrorPage } from '@/components/ErrorPage';
import { NoTenantFound } from '@/components/NoTenantFound';
import { theme } from '@/imports/ui/chakra/theme';
import { MentorshipRouteGuard } from '@/modules/mentorship/common/access/MentorshipRouteGuard';
import { TENANT } from '@/queries';
import { Tenant } from '@/queries/graphql-types/Tenant';
import { Tradewing } from '@/tradewing';
import {
    CommunityMovedInfo,
    fetchCommunityMoved,
} from '@/utils/fetchCommunityMoved';

type CustomAppProps = {
  data: Tenant | null;
  error: Error | null;
  tenantDomain?: string | null;
  communityMoved?: CommunityMovedInfo | null;
};

const analyticsConfig = {
  NCACPA: {
    gaId: 'G-5NPQCKSE44',
    hotjarId: 1095864,
    domains: ['community.ncacpa.org'],
  },
  QRCA: {
    gaId: 'G-F3C9MC6GZX',
    domains: ['www.qrca.org'],
  },
  MinnSPRA: {
    gaId: 'G-7CKKT9N68M',
    domains: ['www.minnspra.org'],
  },
  ACTS: {
    gaId: 'G-YDFTE9S2V6',
    domains: ['community.actscience.org'],
  },
  'PPI Community': {
    gaId: 'G-MKX8K2R39Z',
    domains: ['community.plasticpipe.org'],
  },
};

type AnalyticsKey = keyof typeof analyticsConfig;

const getAnalyticsConfig = ({
  acronym,
  tenantDomain,
}: {
  acronym?: string | null;
  tenantDomain?: string | null;
}) => {
  if (acronym && acronym in analyticsConfig) {
    const key = acronym as AnalyticsKey;

    return {
      key,
      config: analyticsConfig[key],
    };
  }

  const matchedEntry = Object.entries(analyticsConfig).find(([, config]) =>
    config.domains.includes(tenantDomain || '')
  );

  if (!matchedEntry) return null;

  const [key, config] = matchedEntry;

  return {
    key: key as AnalyticsKey,
    config,
  };
};

const AnalyticsScripts = ({
  acronym,
  tenantDomain,
}: {
  acronym?: string | null;
  tenantDomain?: string | null;
}) => {
  const analytics = getAnalyticsConfig({ acronym, tenantDomain });

  if (!analytics) return null;

  const scriptId = analytics.key.toLowerCase();

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${analytics.config.gaId}`}
        strategy="afterInteractive"
      />

      <Script id={`${scriptId}-ga-init`} strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${analytics.config.gaId}');
        `}
      </Script>

      {'hotjarId' in analytics.config && analytics.config.hotjarId && (
        <Script id={`${scriptId}-hotjar`} strategy="afterInteractive">
          {`
            (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:${analytics.config.hotjarId},hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}
        </Script>
      )}
    </>
  );
};

const CustomApp = ({
  Component,
  pageProps,
  data,
  error,
  tenantDomain,
  communityMoved,
}: AppProps & CustomAppProps) => {
  if (error) {
    return (
      <ChakraProvider
        theme={extendTheme({
          ...theme,
          colors: { primary: baseTheme.colors.blue },
        })}
      >
        <ErrorPage />
      </ChakraProvider>
    );
  }

  if (!data?.tenant.result) {
    return (
      <ChakraProvider
        theme={extendTheme({
          ...theme,
          colors: { primary: baseTheme.colors.blue },
        })}
      >
        <NoTenantFound communityMoved={communityMoved} />
      </ChakraProvider>
    );
  }

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1"
        />
        <title>
          {data.tenant.result.acronym || data.tenant.result.displayName}
        </title>
        <link
          rel="shortcut icon"
          href={data.tenant.result.logoIcon?.shortcutIcon || undefined}
        />
        <link
          rel="preload"
          href="/fonts/44393f83cfe6e236d297.ttf"
          as="font"
          type="font/ttf"
          crossOrigin="anonymous"
        />
        <style type="text/css">
          {`
            @font-face {
              font-family: "icomoon";
              src: url("/fonts/44393f83cfe6e236d297.ttf") format("truetype");
            }

            ${
              data.tenant.result.acronym === 'QRCA'
                ? `
              body {
                font-family: Arial, sans-serif !important;
              }
            `
                : ''
            }
          `}
        </style>
        <link
          rel="apple-touch-icon"
          href={data.tenant.result.logoIcon?.appleTouchIcon || undefined}
        />
      </Head>

      <AnalyticsScripts
        acronym={data.tenant.result.acronym}
        tenantDomain={tenantDomain}
      />

      <Script id="embedly-platform" strategy="afterInteractive">
        {`
          (function(w, d) {
            var id = 'embedly-platform', n = 'script';
            if (!d.getElementById(id)) {
              w.embedly = w.embedly || function() {
                (w.embedly.q = w.embedly.q || []).push(arguments);
              };
              var e = d.createElement(n);
              e.id = id;
              e.async = 1;
              e.src = ('https:' === document.location.protocol ? 'https' : 'http') +
                '://cdn.embedly.com/widgets/platform.js';
              var s = d.getElementsByTagName(n)[0];
              s.parentNode.insertBefore(e, s);
            }
          })(window, document);
        `}
      </Script>

      <ApolloProvider client={apolloClient}>
        <Tradewing primaryColor={data.tenant.result.primaryColor}>
          <MentorshipRouteGuard>
            <Component {...pageProps} />
          </MentorshipRouteGuard>
        </Tradewing>
      </ApolloProvider>
    </>
  );
};

const getTenantDomainFromRequest = (
  req: IncomingMessage | undefined | null
): string | undefined => {
  const header =
    req?.headers.tenant_domain ||
    req?.headers['x-forwarded-host'] ||
    req?.headers.host;

  const value = Array.isArray(header) ? header[0] : header;

  return value?.split(',')[0].trim().split(':')[0];
};

const globalCache = new InMemoryCache();

CustomApp.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps & CustomAppProps> => {
  try {
    let uri;
    let cache;

    if (typeof window !== 'undefined') {
      uri = '/graphql2';
      cache = globalCache;
    } else {
      uri = process.env.NEXT_PUBLIC_APOLLO_HTTP_URI;
      cache = new InMemoryCache();
    }

    const client = new ApolloClient({
      ssrMode: typeof window === 'undefined',
      uri,
      cache,
    });

    const req = appContext.ctx.req;

    const tenantDomain =
      typeof window !== 'undefined'
        ? window.location.hostname
        : getTenantDomainFromRequest(req);

    const { data } = await client.query<Tenant>({
      query: TENANT,
      context: {
        headers: {
          tenant_domain: tenantDomain,
        },
      },
      fetchPolicy: 'no-cache',
    });

    return {
      ...(await App.getInitialProps(appContext)),
      data,
      error: null,
      tenantDomain,
      communityMoved: null,
    };
  } catch (error) {
    if (error?.networkError?.statusCode === 422) {
      if (appContext.ctx.res) {
        appContext.ctx.res.statusCode = 404;
      }

      const tenantDomain =
        typeof window !== 'undefined'
          ? window.location.hostname
          : getTenantDomainFromRequest(appContext.ctx.req);

      return {
        ...(await App.getInitialProps(appContext)),
        data: null,
        error: null,
        tenantDomain,
        communityMoved: await fetchCommunityMoved(tenantDomain),
      };
    }

    if (appContext.ctx.res) {
      appContext.ctx.res.statusCode = 500;
    }

    return {
      ...(await App.getInitialProps(appContext)),
      data: null,
      error,
      tenantDomain: null,
      communityMoved: null,
    };
  }
};

export default CustomApp;
